import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Link,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { ArrowBack, Lock, Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient } from '../services/api';
import TeksTimeBrandMark from '../components/TeksTimeBrandMark';

const MIN_LEN = 9;

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const recoveryToken = searchParams.get('recoveryToken');

  const [formData, setFormData] = useState({ newPassword: '', confirmPassword: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (!recoveryToken) {
      setMessage({ type: 'error', text: 'Invalid recovery session. Please start password recovery again.' });
    }
  }, [recoveryToken]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!recoveryToken) return;
    if (formData.newPassword.length < MIN_LEN) {
      setMessage({ type: 'error', text: `Password must be at least ${MIN_LEN} characters long` });
      return;
    }
    if (formData.newPassword !== formData.confirmPassword) {
      setMessage({ type: 'error', text: 'Passwords do not match' });
      return;
    }
    setLoading(true);
    setMessage(null);
    try {
      const response = await apiClient.password.setRecoveredPassword(
        recoveryToken,
        formData.newPassword,
        formData.confirmPassword,
      );
      if (response.data.success && response.data.token && response.data.user) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        setMessage({ type: 'success', text: 'Password updated. Redirecting…' });
        setTimeout(() => navigate('/dashboard'), 800);
      }
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to set password.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <TeksTimeBrandMark />
          <Typography component="h2" variant="h5" gutterBottom align="center">
            Create Your Personal Password
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
            Choose a new password of at least {MIN_LEN} characters. Passphrases are allowed.
          </Typography>
          {message && (
            <Alert severity={message.type} sx={{ mb: 2 }}>{message.text}</Alert>
          )}
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              margin="normal"
              required
              fullWidth
              label="New Password"
              type={showPassword ? 'text' : 'password'}
              value={formData.newPassword}
              onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
              disabled={loading || !recoveryToken}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Confirm Password"
              type={showPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              disabled={loading || !recoveryToken}
            />
            <Button type="submit" fullWidth variant="contained" sx={{ mt: 3 }} disabled={loading || !recoveryToken}
              startIcon={loading ? <CircularProgress size={20} /> : <Lock />}>
              {loading ? 'Saving...' : 'Save New Password'}
            </Button>
          </Box>
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Link component="button" variant="body2" onClick={() => navigate('/forgot-password')}>
              <ArrowBack fontSize="small" /> Start Over
            </Link>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default ResetPasswordPage;
